import { useRef } from "react";
import { motion, ease as mmEase, useInView } from "@/lib/motion";
import { FEATURES } from "../data";

const ez = mmEase.expo;

const GLASS_TEXTURE = "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?w=1400&q=60&auto=format&fit=crop";

function FeatureChapter({ feature: f, reverse, first }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px 0px" });

  return (
    <div ref={ref} className={`py-24 px-6 relative overflow-hidden ${f.dark ? "section-dark" : "bg-white"}`}>
      {/* Abstract glass texture — first feature section only, at 6% opacity */}
      {first && (
        <img
          src={GLASS_TEXTURE}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
            opacity: 0.06,
            pointerEvents: "none",
            mixBlendMode: "multiply",
          }}
        />
      )}
      <div className={`max-w-[1200px] mx-auto flex flex-col gap-12 items-center ${
        reverse ? "lg:flex-row-reverse" : "lg:flex-row"
      }`}>

        {/* Stat visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.88 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.72, ease: ez }}
          className="flex-1 flex items-center justify-center"
        >
          {f.n === "01" ? (
            /* Wallet stack — three stacked currency cards */
            <div style={{ position: "relative", width: "260px", height: "180px" }}>
              {[
                { flag: "🇳🇬", code: "NGN", name: "Naira",           balance: "₦720,000",  color: "#7C3AED", top: 40, left: 24, z: 1 },
                { flag: "🇺🇸", code: "USD", name: "US Dollar",       balance: "$4,800.00", color: "#06B6D4", top: 20, left: 12, z: 2 },
                { flag: "🇬🇧", code: "GBP", name: "Pound Sterling",  balance: "£3,240.00", color: "#10B981", top: 0,  left: 0,  z: 3 },
              ].map((w) => (
                <div
                  key={w.code}
                  style={{
                    position: "absolute",
                    top: `${w.top}px`, left: `${w.left}px`,
                    width: "220px",
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "rgba(11,29,58,0.96)",
                    border: `1px solid ${w.color}30`,
                    zIndex: w.z,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                  }}
                >
                  <div style={{ height: "3px", width: "32px", borderRadius: "2px", background: w.color, marginBottom: "10px" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                    <span style={{ fontSize: "18px" }}>{w.flag}</span>
                    <div>
                      <p style={{ fontSize: "11px", fontWeight: 800, color: "rgba(255,255,255,0.9)", letterSpacing: "0.06em" }}>{w.code}</p>
                      <p style={{ fontSize: "9px", color: "rgba(255,255,255,0.32)", letterSpacing: "0.04em" }}>{w.name}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: "1.3rem", fontWeight: 900, color: "white", letterSpacing: "-0.03em", fontVariantNumeric: "tabular-nums" }}>
                    {w.balance}
                  </p>
                </div>
              ))}
            </div>
          ) : f.n === "02" ? (
            /* Fee comparison — Bank vs MoneyMeet Pay */
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "280px" }}>
              <div style={{ padding: "14px 16px", borderRadius: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)" }}>
                <p style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em", color: "rgba(255,255,255,0.28)", marginBottom: "8px", textTransform: "uppercase" }}>Your bank</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)" }}>Send £500</p>
                    <p style={{ fontSize: "10px", color: "rgba(239,68,68,0.75)", marginTop: "3px" }}>~4% rate markup hidden</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: "1.15rem", fontWeight: 900, color: "rgba(255,255,255,0.45)", fontVariantNumeric: "tabular-nums" }}>₦725,000</p>
                    <p style={{ fontSize: "9px", color: "rgba(255,255,255,0.22)", marginTop: "2px" }}>arrives</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: "14px 16px", borderRadius: "12px", background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.24)" }}>
                <p style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.12em", color: "rgba(16,185,129,0.65)", marginBottom: "8px", textTransform: "uppercase" }}>MoneyMeet Pay</p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.35)" }}>Send £500</p>
                    <p style={{ fontSize: "10px", color: "rgba(16,185,129,0.85)", marginTop: "3px" }}>£2.50 flat fee only</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: "1.15rem", fontWeight: 900, color: "var(--color-success)", fontVariantNumeric: "tabular-nums" }}>₦741,445</p>
                    <p style={{ fontSize: "9px", color: "rgba(16,185,129,0.6)", marginTop: "2px" }}>arrives</p>
                  </div>
                </div>
              </div>
              <p style={{ textAlign: "center", paddingTop: "2px", fontSize: "11px", fontWeight: 800, color: "var(--color-success)", letterSpacing: "0.03em" }}>
                Mama receives ₦16,445 more
              </p>
            </div>
          ) : (
            /* Default circle stat — Feature 03 */
            <div className="relative flex flex-col items-center">
              <div
                className="w-52 h-52 rounded-full flex flex-col items-center justify-center"
                style={{ background: `${f.color}0E`, border: `2px solid ${f.color}22` }}
              >
                <p
                  className="font-display font-black num-tabular leading-none mb-1"
                  style={{
                    fontSize: "clamp(2.2rem, 6vw, 3.8rem)", letterSpacing: "-0.04em",
                    color: f.dark ? "#fff" : f.color,
                  }}
                >
                  {f.stat}
                </p>
                <p
                  className="text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: f.dark ? "rgba(255,255,255,0.38)" : "rgba(11,29,58,0.38)" }}
                >
                  {f.unit}
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Content */}
        <motion.div
          className="flex-1"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, ease: ez, delay: 0.1 }}
        >
          <h2
            className={`font-display font-black leading-[1.08] mb-5 ${f.dark ? "text-white" : "text-navy"}`}
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)", letterSpacing: "-0.035em" }}
          >
            {f.title.split("\n").map((line, i, arr) => (
              <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
            ))}
          </h2>
          <p
            className="text-[1rem] leading-relaxed max-w-md"
            style={{ color: f.dark ? "rgba(255,255,255,0.48)" : "rgba(11,29,58,0.55)" }}
          >
            {f.body}
          </p>
        </motion.div>

      </div>
    </div>
  );
}

export default function FeaturesSection() {
  return (
    <section id="features" className="bg-white">
      {FEATURES.map((f, i) => (
        <FeatureChapter key={f.n} feature={f} reverse={i % 2 !== 0} first={i === 0} />
      ))}
    </section>
  );
}
